import { Injectable, inject } from "@angular/core";
import { first, Observable, of } from "rxjs";
import { JanusServerStatus } from "../interfaces/janus-server-status";
import { HttpClient } from "@angular/common/http";
import { MITPService } from "./mitp.service";
import { IJanusResponse } from "../interfaces/ijanus-response";
import { environment } from "../../environments/environment";
import { ScientificPublication } from "../interfaces/scientific-publication";
import { JanusFormattedProtocol } from "../modules/protocols/entities/janus-formatted-protocol";
import { TranslateService } from "@tolgee/ngx";

export interface ProtocolInteractiveFormCreation {
	type: string;
	html: string;
	js: string;
	message?: string;
}

@Injectable({
	providedIn: "root",
})
export class JanusService {
	private httpClient = inject(HttpClient);
	private mitp = inject(MITPService);
	private translate = inject(TranslateService);

	askQuestion(question: string): Observable<IJanusResponse> {
		if (!environment.production) {
			return of({
				answer: `Mocked answer for: ${question}`,
				question,
				sources: [
					{
						score: 0.679,
						protocol_id: 4,
						title: "Protocol title",
						text_snippet: "#### Text snippet for",
					},
				],
				protocols: [
					{
						id: 4,
						label: "Protocol Label",
						status: 1,
						institute: { id: 1, label: "Institute label" },
						tags: [
							{ id: 46, label: "Gynécologie", section: true, last_change: "2026-03-20T23:15:05+00:00" },
						],
						updates: [],
						authors: [],
						approvers: [],
						managers: [],
						errors: [],
						attachments: [],
						warnings: [],
						approvals: [],
						codification: "",
						last_change: "2026-03-23T12:17:01+00:00",
						current_status: "published",
						sharing: null,
						expiry: null,
						mode: null,
						risk: null,
						pqi: null,
					},
				],
			});
		}

		return this.httpClient
			.post<IJanusResponse>(this.mitp.Janus() + `/ask`, {
				question: question,
			})
			.pipe(first());
	}

	getStatus(): Observable<JanusServerStatus> {
		return this.httpClient.get<JanusServerStatus>(this.mitp.Janus() + "/status").pipe(first());
	}

	searchArticles(query: string, offset: number): Observable<ScientificPublication[]> {
		return this.httpClient.get<ScientificPublication[]>(
			this.mitp.Janus() + `/search?query=${query}&offset=${offset}`
		);
	}

	getPublicationUrl(doi: string) {
		return this.httpClient.get(this.mitp.Janus() + `/papers/${doi}/url`);
	}

	getPublicationTldr(paperId: string) {
		return this.httpClient.get(this.mitp.Janus() + `/papers/${paperId}/tldr`);
	}

	formatProtocol(content: string): Observable<JanusFormattedProtocol> {
		return this.httpClient.post<JanusFormattedProtocol>(this.mitp.Janus() + `/format/protocol`, {
			content: content,
		});
	}

	generateProtocolForm(instructions: string): Observable<ProtocolInteractiveFormCreation> {
		return this.httpClient.post<ProtocolInteractiveFormCreation>(
			this.mitp.Janus() + `/protocols/interactive-form`,
			{
				instructions,
			}
		);
	}

	suggestProtocolRisk(id: number): Observable<unknown> {
		return this.httpClient.post(this.mitp.Janus() + `/protocols/${id}/risk`, {});
	}

	improveImage(base64: string, style?: string): Observable<{ image: string }> {
		return this.httpClient.post<{ image: string }>(this.mitp.Janus() + `/images/quality`, {
			image: base64,
			style: style,
		});
	}

	async streamQuery(
		question: string,
		onToken: (token: string) => void,
		onSources: (sources: { score: number; protocol_id: number; title: string; text_snippet: string }[]) => void,
		onDone: () => void,
		onError: (message: string) => void,
		protocolId?: number
	): Promise<void> {
		const token = sessionStorage.getItem("access_token");
		const body = new URLSearchParams({ question });
		if (protocolId !== undefined) {
			body.set("protocol_id", String(protocolId));
		}

		const response = await fetch(this.mitp.Janus() + "/query/stream", {
			method: "POST",
			headers: {
				"Content-Type": "application/x-www-form-urlencoded",
				...(token ? { Authorization: `Bearer ${token}` } : {}),
			},
			body: body.toString(),
		});

		if (!response.ok) {
			onError(this.translate.instant("janus.error.subscription-required"));
			return;
		}

		if (!response.body) {
			onError(this.translate.instant("janus.error.no-response"));
			return;
		}

		const reader = response.body.getReader();
		const decoder = new TextDecoder();
		let buffer = "";

		while (true) {
			const { done, value } = await reader.read();
			if (done) break;

			buffer += decoder.decode(value, { stream: true });
			const lines = buffer.split("\n");
			buffer = lines.pop() ?? "";

			for (const line of lines) {
				const trimmed = line.trim();
				if (!trimmed.startsWith("data:")) continue;

				const json = trimmed.slice("data:".length).trim();
				if (!json) continue;

				const event = JSON.parse(json);

				if (event.type === "token") {
					onToken(event.token);
				} else if (event.type === "sources") {
					onSources(event.sources ?? []);
				} else if (event.type === "done") {
					onDone();
					reader.cancel();
					return;
				} else if (event.type === "error") {
					onError(event.message ?? "Unknown error");
					reader.cancel();
					return;
				}
			}
		}
	}
}
